import type { AiModelPrice, AiPricingUnit, PriceSyncRun, UnpricedModel } from '@owl/shared';
import { useCallback, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import { useToast } from '../components/Toast';
import { Icon } from '../components/icons';
import { ApiError, api } from '../lib/api';
import { timeAgo } from '../lib/time';
import { useLoader } from '../lib/useLoader';

function formatUsdAmount(n: number): string {
  if (n === 0) return '$0.00';
  if (n < 0.0001) return `$${n.toFixed(6)}`;
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

// Render a stored "per 1M of unit" value in the unit's natural form.
function formatRateForUnit(value: number, unit: AiPricingUnit): string {
  if (value === 0) return '—';
  if (unit === 'audio_seconds') {
    // value is USD per 1M seconds → USD per hour = value * 3600 / 1_000_000.
    const perHour = (value * 3600) / 1_000_000;
    return `${formatUsdAmount(perHour)}/h`;
  }
  if (unit === 'characters') {
    return `${formatUsdAmount(value)}/1M ch`;
  }
  return `${formatUsdAmount(value)}/1M tok`;
}

function formatInt(n: number): string {
  return new Intl.NumberFormat('es-ES').format(n);
}

type Tab = 'prices' | 'unpriced' | 'runs';

const SOURCE_LABELS: Record<string, string> = {
  'official-api': 'Official',
  litellm: 'LiteLLM',
  manual: 'Manual',
  estimated: 'Estimated',
};

const SOURCE_COLORS: Record<string, string> = {
  'official-api': '#3FBFB1',
  litellm: '#7DB0FF',
  manual: '#F08A5D',
  estimated: '#D4B15C',
};

const UNIT_LABELS: Record<AiPricingUnit, string> = {
  tokens: 'Tokens',
  audio_seconds: 'Audio',
  characters: 'Chars',
};

const UNIT_COLORS: Record<AiPricingUnit, string> = {
  tokens: '#7DB0FF',
  audio_seconds: '#C997FF',
  characters: '#7AD8B0',
};

function SourceBadge({ source }: { source: string }) {
  const bg = SOURCE_COLORS[source] ?? '#888';
  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 6,
        background: bg,
        color: '#0c0c0c',
        fontSize: 11,
        fontWeight: 600,
      }}
      title={`Source: ${SOURCE_LABELS[source] ?? source}`}
    >
      {SOURCE_LABELS[source] ?? source}
    </span>
  );
}

function UnitBadge({ unit }: { unit: AiPricingUnit }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 4,
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 6,
        background: UNIT_COLORS[unit],
        color: '#0c0c0c',
        fontSize: 11,
        fontWeight: 600,
      }}
      title={`Pricing unit: ${unit}`}
    >
      {UNIT_LABELS[unit]}
    </span>
  );
}

export function AiPrices({ ctx }: { ctx: ShellCtx }) {
  const refreshTick = ctx.refreshTick;
  const [tab, setTab] = useState<Tab>('prices');
  const [prices, setPrices] = useState<AiModelPrice[] | null>(null);
  const [unpriced, setUnpriced] = useState<UnpricedModel[] | null>(null);
  const [runs, setRuns] = useState<PriceSyncRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const { push } = useToast();

  const loadPrices = useCallback(async () => {
    try {
      const r = await api.listAiPrices();
      setPrices(r.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prices');
    }
  }, []);

  const loadUnpriced = useCallback(async () => {
    try {
      const r = await api.listUnpricedModels();
      setUnpriced(r.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load unpriced');
    }
  }, []);

  const loadRuns = useCallback(async () => {
    try {
      const r = await api.listAiPriceSyncRuns(50);
      setRuns(r.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, []);

  const load = useCallback(() => {
    if (tab === 'prices') void loadPrices();
    else if (tab === 'unpriced') void loadUnpriced();
    else void loadRuns();
  }, [tab, loadPrices, loadUnpriced, loadRuns]);

  useLoader(load, refreshTick);

  async function syncNow() {
    setSyncing(true);
    try {
      const r = await api.syncAiPricesNow();
      push(
        `Sync done · +${r.added} new, ${r.changed} changed, ${r.unchanged} unchanged, ${r.skippedLowerRank} skipped`,
      );
      await Promise.all([loadPrices(), loadRuns(), loadUnpriced()]);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  }

  async function confirmEstimate(item: UnpricedModel) {
    if (!item.suggestedFrom) return;
    try {
      await api.upsertAiPrice({
        provider: item.provider,
        model: item.model,
        inputPricePer1m: item.suggestedFrom.inputPricePer1m,
        outputPricePer1m: item.suggestedFrom.outputPricePer1m,
        cachedInputPricePer1m: item.suggestedFrom.cachedInputPricePer1m,
        pricingUnit: item.suggestedFrom.pricingUnit,
        source: 'estimated',
      });
      push(`${item.provider}/${item.model} priced from ${item.suggestedFrom.model}`);
      await Promise.all([loadUnpriced(), loadPrices()]);
    } catch (err) {
      push(err instanceof ApiError ? err.message : 'Failed to confirm estimate');
    }
  }

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div className="view-title">AI prices</div>
          <div className="view-sub">
            Prices for chat tokens, speech-to-text audio and text-to-speech characters. Priority:
            Official &gt; LiteLLM &gt; Manual &gt; Estimated. Manual entries act as fallback when no
            automatic source covers the model.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="cta" disabled={syncing} onClick={syncNow}>
            {syncing ? (
              'Syncing…'
            ) : (
              <>
                <Icon.Refresh size={12} /> Sync now
              </>
            )}
          </button>
        </div>
      </div>

      <div className="range-picker" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`range-opt ${tab === 'prices' ? 'active' : ''}`}
          onClick={() => setTab('prices')}
        >
          Prices ({prices?.length ?? '—'})
        </button>
        <button
          type="button"
          className={`range-opt ${tab === 'unpriced' ? 'active' : ''}`}
          onClick={() => setTab('unpriced')}
        >
          Unpriced ({unpriced?.length ?? '—'})
        </button>
        <button
          type="button"
          className={`range-opt ${tab === 'runs' ? 'active' : ''}`}
          onClick={() => setTab('runs')}
        >
          Sync runs
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {tab === 'prices' &&
        (prices === null ? (
          <div className="empty-state">Loading…</div>
        ) : prices.length === 0 ? (
          <div className="empty-state">
            No prices yet. Click "Sync now" to populate from LiteLLM.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider / Model</th>
                  <th>Unit</th>
                  <th>Input rate</th>
                  <th>Output rate</th>
                  <th>Cached rate</th>
                  <th>Source</th>
                  <th>Last sync</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {prices.map((p) => (
                  <tr key={p.id} className="data-row">
                    <td>
                      <span className="dim">{p.provider}</span> · <strong>{p.model}</strong>
                    </td>
                    <td>
                      <UnitBadge unit={p.pricingUnit} />
                    </td>
                    <td className="mono">{formatRateForUnit(p.inputPricePer1m, p.pricingUnit)}</td>
                    <td className="mono">{formatRateForUnit(p.outputPricePer1m, p.pricingUnit)}</td>
                    <td className="mono">
                      {p.cachedInputPricePer1m === null
                        ? '—'
                        : formatRateForUnit(p.cachedInputPricePer1m, p.pricingUnit)}
                    </td>
                    <td>
                      <SourceBadge source={p.source} />
                    </td>
                    <td className="dim">{p.lastSyncedAt ? timeAgo(p.lastSyncedAt) : 'never'}</td>
                    <td className="dim">{timeAgo(p.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'unpriced' &&
        (unpriced === null ? (
          <div className="empty-state">Loading…</div>
        ) : unpriced.length === 0 ? (
          <div className="empty-state">
            Nothing pending — every detected model has an exact price.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider / Model</th>
                  <th>Events 7d</th>
                  <th>Suggested from</th>
                  <th>Unit</th>
                  <th>Input rate</th>
                  <th>Output rate</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {unpriced.map((m) => (
                  <tr key={`${m.provider}|${m.model}`} className="data-row">
                    <td>
                      <span className="dim">{m.provider}</span> · <strong>{m.model}</strong>
                      <div className="dim" style={{ fontSize: 11 }}>
                        {m.displayLabel}
                      </div>
                    </td>
                    <td className="mono">{formatInt(m.events7d)}</td>
                    <td>
                      {m.suggestedFrom ? (
                        <span className="mono dim">{m.suggestedFrom.model}</span>
                      ) : (
                        <span className="dim">no candidate</span>
                      )}
                    </td>
                    <td>
                      {m.suggestedFrom ? <UnitBadge unit={m.suggestedFrom.pricingUnit} /> : '—'}
                    </td>
                    <td className="mono">
                      {m.suggestedFrom
                        ? formatRateForUnit(
                            m.suggestedFrom.inputPricePer1m,
                            m.suggestedFrom.pricingUnit,
                          )
                        : '—'}
                    </td>
                    <td className="mono">
                      {m.suggestedFrom
                        ? formatRateForUnit(
                            m.suggestedFrom.outputPricePer1m,
                            m.suggestedFrom.pricingUnit,
                          )
                        : '—'}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="cta"
                        disabled={!m.suggestedFrom}
                        onClick={() => confirmEstimate(m)}
                        title="Persist the suggested price as a manual entry for this exact model"
                      >
                        Confirm
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

      {tab === 'runs' &&
        (runs === null ? (
          <div className="empty-state">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="empty-state">No sync runs yet.</div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>Source</th>
                  <th>Added</th>
                  <th>Changed</th>
                  <th>Unchanged</th>
                  <th>Skipped (lower rank)</th>
                  <th>Errors</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const dur =
                    r.finishedAt && r.startedAt
                      ? `${(
                          (new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime()) /
                            1000
                        ).toFixed(1)}s`
                      : '…';
                  return (
                    <tr key={r.id} className="data-row">
                      <td className="dim">{timeAgo(r.startedAt)}</td>
                      <td className="mono">{dur}</td>
                      <td className="mono">{r.sourceName}</td>
                      <td className="mono">{r.modelsAdded}</td>
                      <td className="mono">{r.modelsChanged}</td>
                      <td className="mono">{r.modelsUnchanged}</td>
                      <td className="mono">{r.modelsSkippedLowerRank}</td>
                      <td className="mono">{r.errors.length}</td>
                      <td>
                        <span
                          style={{
                            color: r.ok ? '#5CB377' : r.finishedAt ? '#E26676' : '#D4B15C',
                            fontWeight: 600,
                          }}
                        >
                          {r.finishedAt ? (r.ok ? 'OK' : 'Errors') : 'Running'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
